import type { ReactNode } from 'react'

import { cn } from '../utilitarios/cn'

export interface PropriedadesLinhaDoTempo {
  children: ReactNode
  className?: string
}

/**
 * Casca da timeline da TELA-05 — aba "Interação do Lead".
 *
 * Só estrutura visual: o que cada tipo de interação mostra é domínio e mora no
 * `apps/web`. A timeline é **append-only** (RN-25), então nenhum item aqui tem
 * ação de editar ou excluir — não é omissão de escopo, é a regra.
 */
export function LinhaDoTempo({ children, className }: PropriedadesLinhaDoTempo) {
  return (
    <ol className={cn('relative flex flex-col', className)}>
      <span
        aria-hidden="true"
        className="absolute bottom-2 left-[0.9375rem] top-2 w-px bg-borda"
      />
      {children}
    </ol>
  )
}

export interface PropriedadesItemLinhaDoTempo {
  /** Marcador na trilha — ícone do tipo de interação. */
  marcador: ReactNode
  tomMarcador?: 'neutro' | 'marca' | 'sucesso' | 'atencao' | 'perigo' | 'info'
  titulo: ReactNode
  /** Data/hora formatada em pt-BR. */
  quando: string
  autor?: ReactNode
  children?: ReactNode
  className?: string
}

const TONS = {
  neutro: 'bg-superficie-3 text-conteudo-suave ring-borda',
  marca: 'bg-marca-50 text-marca-600 ring-marca-200',
  sucesso: 'bg-sucesso-suave text-sucesso ring-sucesso/30',
  atencao: 'bg-atencao-suave text-atencao ring-atencao/30',
  perigo: 'bg-perigo-suave text-perigo ring-perigo/30',
  info: 'bg-info-suave text-info ring-info/30',
} as const

export function ItemLinhaDoTempo({
  marcador,
  tomMarcador = 'neutro',
  titulo,
  quando,
  autor,
  children,
  className,
}: PropriedadesItemLinhaDoTempo) {
  return (
    <li className={cn('relative flex gap-3 pb-5 last:pb-0', className)}>
      <span
        className={cn(
          'relative z-10 mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ring-1',
          TONS[tomMarcador],
        )}
      >
        {marcador}
      </span>

      <div className="min-w-0 flex-1 pt-1">
        <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
          <p className="text-sm font-semibold text-conteudo">{titulo}</p>
          <time className="shrink-0 text-xs tabular-nums text-conteudo-fraco">{quando}</time>
        </div>
        {autor ? <p className="mt-0.5 text-xs text-conteudo-suave">{autor}</p> : null}
        {children ? <div className="mt-2">{children}</div> : null}
      </div>
    </li>
  )
}
